package com.schoolconnect.app.designsystem

import androidx.compose.foundation.BorderStroke
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.collect
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.calendar
import ischoolconnect.sharedui.generated.resources.clock
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.chevron_up
import ischoolconnect.sharedui.generated.resources.chevron_left
import ischoolconnect.sharedui.generated.resources.chevron_right
import ischoolconnect.sharedui.generated.resources.close_x
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import kotlin.time.Clock
import kotlin.math.roundToInt
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.PI

internal enum class AppDialogSize {
    Compact,
    Narrow,
    Standard,
    Wide
}

internal data class AppDialogColors(
    // The UI bible specifies a restrained 10% navy scrim behind dialogs.
    val overlay: Color = Color(0x1A0F172A),
    val surface: Color = AppSurface,
    val primary: Color = AppPrimary,
    val navy: Color = AppNavy,
    val muted: Color = AppMuted,
    val border: Color = Color(0xFFCBD5E1),
    val inputBorder: Color = AppInputBorder,
    val danger: Color = AppDanger,
    val iconTile: Color = AppPrimarySoft,
    val selectedBackground: Color = AppSelectedBg
)

internal enum class AppDialogActionStyle {
    Primary,
    Secondary,
    Danger
}

internal data class AppDialogFooterAction(
    val label: String,
    val onClick: () -> Unit,
    val style: AppDialogActionStyle = AppDialogActionStyle.Secondary,
    val enabled: Boolean = true,
    val width: Dp = AppDialogButtonWidth
)

internal val AppDialogDefaultColors = AppDialogColors()
internal val AppDialogShape = RoundedCornerShape(8.dp)
internal val AppDialogFieldShape = RoundedCornerShape(AppDialogControlCornerRadius)
internal val AppDialogDropdownShape = RoundedCornerShape(8.dp)

/**
 * A dialog callback must not remove its Popup synchronously from the pointer
 * event that invoked it. Doing so can detach the dispatching modifier node
 * before Compose has finished routing the event, which produces
 * "Modifier node is not currently attached" on web and desktop.
 *
 * Waiting for the next frame keeps the Popup attached for the complete input
 * dispatch cycle. The callback is also coalesced so a close button cannot
 * schedule multiple dismissals from a double click.
 */
@Composable
internal fun rememberDeferredDialogAction(action: () -> Unit): () -> Unit {
    val scope = rememberCoroutineScope()
    val currentAction = rememberUpdatedState(action)
    val pending = remember { mutableStateOf(false) }

    return remember(scope) {
        {
            if (!pending.value) {
                pending.value = true
                scope.launch {
                    try {
                        withFrameNanos { }
                        currentAction.value()
                    } finally {
                        pending.value = false
                    }
                }
            }
        }
    }
}

@Composable
internal fun AppDialogScaffold(
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    size: AppDialogSize = AppDialogSize.Standard,
    dialogWidthOverride: Dp? = null,
    colors: AppDialogColors = AppDialogDefaultColors,
    shape: Shape = AppDialogShape,
    maxDialogHeight: Dp = AppDialogMaxHeight,
    viewportFraction: Float = 0.85f,
    fillDialogHeight: Boolean = false,
    dismissOnClickOutside: Boolean = true,
    isLoading: Boolean = false,
    loadingTitle: String = "Saving changes",
    content: @Composable ColumnScope.() -> Unit
) {
    val dismissAfterInteraction = rememberDeferredDialogAction(onDismiss)
    val dragOffset = remember { mutableStateOf(IntOffset.Zero) }
    var hasEntered by remember { mutableStateOf(false) }
    // Wait one frame so the first paint stays fully hidden (avoids a solid
    // white dialog flash before the scrim exists on web).
    LaunchedEffect(Unit) {
        withFrameNanos { }
        hasEntered = true
    }
    // Fade scrim + dialog together whenever this scaffold is composed.
    val dialogProgress by animateFloatAsState(
        targetValue = if (hasEntered) 1f else 0f,
        animationSpec = tween(durationMillis = 220, easing = FastOutSlowInEasing),
        label = "dialog-show"
    )
    val scrimProgress by animateFloatAsState(
        targetValue = if (hasEntered) 1f else 0f,
        animationSpec = tween(durationMillis = 180, easing = FastOutSlowInEasing),
        label = "dialog-scrim-show"
    )
    val popupPositionProvider = remember(dragOffset.value) {
        object : PopupPositionProvider {
            override fun calculatePosition(
                anchorBounds: IntRect,
                windowSize: IntSize,
                layoutDirection: LayoutDirection,
                popupContentSize: IntSize
            ): IntOffset {
                val centeredX = ((windowSize.width - popupContentSize.width) / 2) + dragOffset.value.x
                val centeredY = ((windowSize.height - popupContentSize.height) / 2) + dragOffset.value.y
                val maxX = (windowSize.width - popupContentSize.width).coerceAtLeast(0)
                val maxY = (windowSize.height - popupContentSize.height).coerceAtLeast(0)
                return IntOffset(
                    x = centeredX.coerceIn(0, maxX),
                    y = centeredY.coerceIn(0, maxY)
                )
            }
        }
    }

    // Keep the application visible beneath a restrained navy scrim while the
    // dialog surface remains an opaque white layer above it.
    Popup(
        onDismissRequest = {},
        popupPositionProvider = popupPositionProvider,
        properties = PopupProperties(
            focusable = false,
            dismissOnBackPress = false,
            dismissOnClickOutside = false
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .graphicsLayer { alpha = scrimProgress }
                .background(colors.overlay)
        )
    }

    Popup(
        onDismissRequest = dismissAfterInteraction,
        properties = PopupProperties(
            focusable = true,
            dismissOnBackPress = true,
            dismissOnClickOutside = dismissOnClickOutside
        ),
        popupPositionProvider = popupPositionProvider
    ) {
        BoxWithConstraints(
            modifier = Modifier.padding(AppDialogHorizontalMargin)
        ) {
            val targetWidth = dialogWidthOverride ?: when (size) {
                AppDialogSize.Compact -> AppDialogCompactWidth
                AppDialogSize.Narrow -> AppDialogNarrowWidth
                AppDialogSize.Standard -> AppDialogStandardWidth
                AppDialogSize.Wide -> AppDialogWideWidth
            }
            // Keep dialogs within the viewport while allowing data-heavy
            // dialogs to request a larger share of the available space.
            val safeViewportFraction = viewportFraction.coerceIn(0.5f, 0.98f)
            val viewportWidth = maxWidth + (AppDialogHorizontalMargin * 2)
            val dialogWidth = minOf(
                targetWidth,
                AppDialogMaxWidth,
                viewportWidth * safeViewportFraction,
                maxWidth.coerceAtLeast(0.dp)
            )
            val dialogHeight = minOf(
                maxDialogHeight,
                maxHeight.coerceAtLeast(0.dp) * safeViewportFraction
            )

            Surface(
                modifier = modifier
                    .graphicsLayer {
                        // Fade with the scrim so the white surface never appears
                        // alone for a frame. Scale in lightly as opacity rises.
                        alpha = dialogProgress
                        scaleX = 0.94f + (0.06f * dialogProgress)
                        scaleY = 0.94f + (0.06f * dialogProgress)
                    }
                    .shadow(
                        elevation = 4.dp,
                        shape = shape,
                        clip = false
                    )
                    .pointerInput(dragOffset) {
                        detectDragGestures(
                            onDrag = { change, dragAmount ->
                                change.consume()
                                val current = dragOffset.value
                                dragOffset.value = IntOffset(
                                    x = current.x + dragAmount.x.roundToInt(),
                                    y = current.y + dragAmount.y.roundToInt()
                                )
                            }
                        )
                    }
                    .width(dialogWidth)
                    .then(if (fillDialogHeight) Modifier.height(dialogHeight) else Modifier.heightIn(max = dialogHeight)),
                color = colors.surface,
                contentColor = colors.navy,
                shape = shape,
                border = BorderStroke(1.dp, colors.border),
                shadowElevation = 0.dp,
                tonalElevation = 0.dp
            ) {
                Box(modifier = if (fillDialogHeight) Modifier.fillMaxSize() else Modifier.fillMaxWidth()) {
                    Column(modifier = if (fillDialogHeight) Modifier.fillMaxSize() else Modifier.fillMaxWidth()) {
                        content()
                    }
                    if (isLoading) {
                        ApiLoadingOverlay(
                            isVisible = true,
                            modifier = Modifier.matchParentSize(),
                            title = loadingTitle,
                            subtitle = "Please wait while the server completes the request."
                        )
                    }
                }
            }
        }
    }
}

@Composable
internal fun AppDialogHeader(
    title: String,
    subtitle: String,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
    icon: DrawableResource? = null,
    titleSuffix: String? = null,
    titleStatus: String? = null,
    titleStatusColor: Color = AppDialogDefaultColors.primary,
    titleColor: Color = AppDialogDefaultColors.primary,
    titleSuffixSeparator: String = " • ",
    titleSuffixFontSize: TextUnit = 20.sp,
    iconContentDescription: String? = null,
    closeContentDescription: String = "Close dialog",
    colors: AppDialogColors = AppDialogDefaultColors,
    height: Dp = AppDialogHeaderHeight,
    iconShape: Shape = RoundedCornerShape(10.dp),
    iconTileSize: Dp = 44.dp,
    iconSize: Dp = 24.dp
) {
    val headerMinHeight = minOf(height, AppDialogHeaderHeight)
    val deferredOnClose = rememberDeferredDialogAction(onClose)

    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = headerMinHeight)
            .padding(start = 24.dp, end = 20.dp)
            .padding(vertical = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            modifier = Modifier
                .weight(1f),
            horizontalArrangement = Arrangement.spacedBy(if (icon == null) 0.dp else 16.dp),
            verticalAlignment = Alignment.Top
        ) {
            if (icon != null) {
                Box(
                    modifier = Modifier
                        .size(iconTileSize)
                        .background(colors.iconTile, iconShape),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = painterResource(icon),
                        contentDescription = iconContentDescription,
                        tint = colors.primary,
                        modifier = Modifier.size(iconSize)
                    )
                }
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = title,
                        color = titleColor,
                        fontSize = AppDialogHeaderTitleSize,
                        lineHeight = AppDialogHeaderTitleLineHeight,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    if (!titleSuffix.isNullOrBlank()) {
                        Text(
                            text = "$titleSuffixSeparator$titleSuffix",
                            color = colors.navy,
                            fontSize = titleSuffixFontSize,
                            lineHeight = AppDialogHeaderTitleLineHeight,
                            fontWeight = FontWeight.Medium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    if (!titleStatus.isNullOrBlank()) {
                        Spacer(Modifier.width(12.dp))
                        Box(Modifier.size(10.dp).background(titleStatusColor, CircleShape))
                        Spacer(Modifier.width(6.dp))
                        Text(titleStatus, color = titleStatusColor, fontSize = titleSuffixFontSize, fontWeight = FontWeight.Medium, maxLines = 1)
                    }
                }
                Text(
                    text = subtitle,
                    color = colors.muted,
                    fontSize = AppDialogHeaderSubtitleSize,
                    lineHeight = AppDialogHeaderSubtitleLineHeight,
                    fontWeight = FontWeight.Medium,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }

        Box(
            modifier = Modifier
                .size(32.dp)
                .clickable(onClick = deferredOnClose),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(Res.drawable.close_x),
                contentDescription = closeContentDescription,
                tint = colors.muted,
                modifier = Modifier.size(24.dp)
            )
        }
    }
}

/**
 * Shared save-time validation summary. Field controls still render their own
 * messages; this gives the user an immediate, form-level explanation without
 * taking over the dialog's scrollable content.
 */
@Composable
internal fun AppDialogValidationSummary(
    errors: List<String>,
    colors: AppDialogColors = AppDialogDefaultColors,
    modifier: Modifier = Modifier
) {
    val visibleErrors = errors.filter { it.isNotBlank() }.distinct()
    if (visibleErrors.isEmpty()) return
    // Field-level validation belongs below each field. Do not introduce a
    // second alert or popup that makes users hunt for the actual error.
    return
    var popupVisible by remember { mutableStateOf(true) }
    var anchorPosition by remember { mutableStateOf(IntOffset.Zero) }
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val popupPositionProvider = remember(anchorPosition, anchorSize) {
        object : PopupPositionProvider {
            override fun calculatePosition(
                anchorBounds: IntRect,
                windowSize: IntSize,
                layoutDirection: LayoutDirection,
                popupContentSize: IntSize
            ): IntOffset {
                val centeredX = anchorPosition.x +
                    ((anchorSize.width - popupContentSize.width) / 2)
                val belowAnchorY = anchorPosition.y + anchorSize.height + 8
                val maxX = (windowSize.width - popupContentSize.width).coerceAtLeast(0)
                val maxY = (windowSize.height - popupContentSize.height).coerceAtLeast(0)
                return IntOffset(
                    x = centeredX.coerceIn(0, maxX),
                    y = belowAnchorY.coerceIn(0, maxY)
                )
            }
        }
    }

    Surface(
        modifier = modifier
            .fillMaxWidth()
            .onGloballyPositioned { coordinates ->
                val position = coordinates.positionInWindow()
                anchorPosition = IntOffset(
                    x = position.x.roundToInt(),
                    y = position.y.roundToInt()
                )
                anchorSize = coordinates.size
            },
        shape = RoundedCornerShape(8.dp),
        color = colors.danger.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, colors.danger.copy(alpha = 0.28f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { popupVisible = true }
                .padding(horizontal = 14.dp, vertical = 9.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "${visibleErrors.size} fields need attention",
                color = colors.danger,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold
            )
            Text(
                text = "View details",
                color = colors.danger,
                fontSize = 12.sp,
                fontWeight = FontWeight.Bold
            )
        }
    }

    if (popupVisible && anchorSize != IntSize.Zero) {
        Popup(
            onDismissRequest = { popupVisible = false },
            popupPositionProvider = popupPositionProvider,
            properties = PopupProperties(
                focusable = true,
                dismissOnBackPress = true,
                dismissOnClickOutside = true
            )
        ) {
            Surface(
                modifier = Modifier
                    .widthIn(min = 280.dp, max = 520.dp)
                    .heightIn(max = 420.dp),
                shape = RoundedCornerShape(12.dp),
                color = colors.surface,
                border = BorderStroke(1.dp, colors.danger.copy(alpha = 0.35f)),
                shadowElevation = 16.dp
            ) {
                Column(
                    modifier = Modifier
                        .padding(horizontal = 18.dp, vertical = 14.dp)
                        .verticalScroll(rememberScrollState()),
                    verticalArrangement = Arrangement.spacedBy(7.dp)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "Please correct these fields (${visibleErrors.size})",
                            color = colors.danger,
                            fontSize = 14.sp,
                            fontWeight = FontWeight.ExtraBold
                        )
                        Box(
                            modifier = Modifier
                                .size(32.dp)
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { popupVisible = false }
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = "×",
                                color = colors.navy,
                                fontSize = 22.sp,
                                fontWeight = FontWeight.Medium
                            )
                        }
                    }
                    visibleErrors.forEach { error ->
                        Text(
                            text = "• $error",
                            color = colors.danger,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }
        }
    }
}

@Composable
internal fun AppDialogErrorMessage(
    message: String,
    colors: AppDialogColors = AppDialogDefaultColors,
    modifier: Modifier = Modifier
) {
    val normalizedMessage = message.trim()
    if (normalizedMessage.isBlank()) return

    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
        color = AppDangerSoft,
        border = BorderStroke(1.dp, AppDangerBorder)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier.size(22.dp).background(colors.danger, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Text("!", color = Color.White, fontSize = 13.sp, fontWeight = FontWeight.Bold)
            }
            Text(
                text = normalizedMessage,
                color = colors.navy,
                fontSize = 14.sp,
                fontWeight = FontWeight.Normal,
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
internal fun AppDialogDivider(colors: AppDialogColors = AppDialogDefaultColors) {
    HorizontalDivider(color = AppSeparator, thickness = 1.dp)
}

/** Lane for the overlay scrollbar. It sits outside the body padding so the bar does not eat the right gap. */
private val AppDialogScrollbarLane = 12.dp

@Composable
internal fun ColumnScope.AppDialogBody(
    modifier: Modifier = Modifier,
    compactBreakpoint: Dp = 820.dp,
    colors: AppDialogColors = AppDialogDefaultColors,
    horizontalPadding: Dp = 28.dp,
    compactHorizontalPadding: Dp = 20.dp,
    verticalPadding: Dp = 24.dp,
    fillAvailable: Boolean = false,
    showVerticalScrollbar: Boolean = false,
    content: @Composable ColumnScope.(compact: Boolean) -> Unit
) {
    val scrollState = rememberScrollState()
    // Reserve the lane only while the bar is actually shown, so a short form keeps equal side padding.
    val scrollbarVisible by remember(showVerticalScrollbar, scrollState) {
        derivedStateOf { showVerticalScrollbar && scrollState.maxValue > 0 }
    }
    BoxWithConstraints(
        modifier = modifier
            .fillMaxWidth()
            .weight(1f, fill = fillAvailable)
    ) {
        val compact = maxWidth < compactBreakpoint
        val sidePadding = if (compact) compactHorizontalPadding else horizontalPadding
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(scrollState)
                .padding(
                    start = sidePadding,
                    top = verticalPadding,
                    end = sidePadding + if (scrollbarVisible) AppDialogScrollbarLane else 0.dp,
                    bottom = verticalPadding
                ),
            verticalArrangement = Arrangement.spacedBy(AppDialogSectionSpacing)
        ) {
            content(compact)
        }
        if (scrollbarVisible) {
            AppDialogVerticalScrollbar(
                state = scrollState,
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .fillMaxHeight()
                    .width(AppDialogScrollbarLane)
                    .padding(top = 8.dp, bottom = 8.dp, end = 2.dp)
            )
        }
    }
}

@Composable
private fun AppDialogVerticalScrollbar(
    state: androidx.compose.foundation.ScrollState,
    modifier: Modifier = Modifier
) {
    val density = LocalDensity.current
    val minThumbPx = with(density) { 28.dp.toPx() }
    val thumbWidthPx = with(density) { 6.dp.toPx() }
    val canScroll by remember(state) { derivedStateOf { state.maxValue > 0 } }
    var dragGrabOffsetPx by remember { mutableStateOf(0f) }
    if (!canScroll) return

    Box(
        modifier = modifier.pointerInput(state) {
            detectDragGestures(
                onDragStart = { offset ->
                    val viewport = size.height.toFloat()
                    val content = viewport + state.maxValue
                    val thumbHeight = (viewport * viewport / content).coerceIn(minThumbPx, viewport)
                    val thumbTop = (viewport - thumbHeight) * state.value / state.maxValue.coerceAtLeast(1)
                    dragGrabOffsetPx = (offset.y - thumbTop).coerceIn(0f, thumbHeight)
                },
                onDrag = { change, _ ->
                    change.consume()
                    val viewport = size.height.toFloat()
                    val content = viewport + state.maxValue
                    val thumbHeight = (viewport * viewport / content).coerceIn(minThumbPx, viewport)
                    val maxTop = (viewport - thumbHeight).coerceAtLeast(1f)
                    val desiredTop = (change.position.y - dragGrabOffsetPx).coerceIn(0f, maxTop)
                    val desiredScroll = desiredTop / maxTop * state.maxValue
                    state.dispatchRawDelta(desiredScroll - state.value)
                },
                onDragEnd = { dragGrabOffsetPx = 0f },
                onDragCancel = { dragGrabOffsetPx = 0f }
            )
        }.drawWithContent {
            if (!canScroll || size.height <= 0f) return@drawWithContent
            val viewport = size.height
            val content = viewport + state.maxValue
            val thumbHeight = (viewport * viewport / content).coerceIn(minThumbPx, viewport)
            val maxTop = (viewport - thumbHeight).coerceAtLeast(0f)
            val thumbTop = if (state.maxValue == 0) 0f else maxTop * state.value / state.maxValue
            drawRoundRect(
                color = Color(0xFFF1F1F1),
                topLeft = Offset((size.width - thumbWidthPx) / 2f, 0f),
                size = Size(thumbWidthPx, viewport),
                cornerRadius = CornerRadius(thumbWidthPx / 2f)
            )
            drawRoundRect(
                color = Color(0xFF7A7A7A).copy(alpha = 0.9f),
                topLeft = Offset((size.width - thumbWidthPx) / 2f, thumbTop),
                size = Size(thumbWidthPx, thumbHeight),
                cornerRadius = CornerRadius(thumbWidthPx / 2f)
            )
        }
    )
}

@Composable
internal fun AppDialogFooter(
    actions: List<AppDialogFooterAction>,
    modifier: Modifier = Modifier,
    colors: AppDialogColors = AppDialogDefaultColors,
    equalWeightButtons: Boolean = false,
    footerHeight: Dp = AppDialogFooterHeight,
    contentPadding: PaddingValues = PaddingValues(horizontal = 28.dp),
    horizontalArrangement: Arrangement.Horizontal = Arrangement.End,
    leadingContent: (@Composable () -> Unit)? = null
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .height(footerHeight)
            .background(AppSoftSurface)
    ) {
        HorizontalDivider(color = AppSeparator, thickness = 1.dp)
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .padding(contentPadding)
        ) {
            val compact = maxWidth < 620.dp
            if (compact || equalWeightButtons) {
                Row(
                    modifier = Modifier.fillMaxSize(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    actions.forEach { action ->
                        AppDialogFooterButton(
                            action = action,
                            colors = colors,
                            // Confirmation and compact dialog actions share the available
                            // width evenly so primary actions do not overpower Cancel/Close.
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
            } else {
                Row(
                    modifier = Modifier.fillMaxSize(),
                    horizontalArrangement = horizontalArrangement,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    leadingContent?.invoke()
                    if (leadingContent != null) {
                        Spacer(modifier = Modifier.weight(1f))
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        actions.forEach { action ->
                            AppDialogFooterButton(
                                action = action,
                                colors = colors,
                                modifier = Modifier.width(action.width)
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
internal fun AppDialogFooterButton(
    action: AppDialogFooterAction,
    colors: AppDialogColors = AppDialogDefaultColors,
    modifier: Modifier = Modifier
) {
    val actionScope = rememberCoroutineScope()
    val deferredAction: () -> Unit = {
        actionScope.launch {
            // Keep the Popup attached until the button's pointer event has
            // completed before a dialog callback removes it.
            withFrameNanos { }
            action.onClick()
        }
    }
    val filled = action.style == AppDialogActionStyle.Primary || action.style == AppDialogActionStyle.Danger
    val buttonColor = if (action.style == AppDialogActionStyle.Danger) colors.danger else colors.primary
    if (filled) {
        Button(
            onClick = deferredAction,
            modifier = modifier.height(AppDialogButtonHeight),
            enabled = action.enabled,
            shape = AppDialogFieldShape,
            colors = ButtonDefaults.buttonColors(
                containerColor = buttonColor,
                contentColor = Color.White,
                disabledContainerColor = Color(0xFF9CA3AF),
                disabledContentColor = Color.White
            ),
            contentPadding = PaddingValues(horizontal = 16.dp)
        ) {
            Text(
                text = action.label,
                fontSize = AppDialogButtonTextSize,
                fontWeight = FontWeight.Medium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    } else {
        OutlinedButton(
            onClick = deferredAction,
            modifier = modifier.height(AppDialogButtonHeight),
            enabled = action.enabled,
            shape = AppDialogFieldShape,
            border = BorderStroke(1.dp, colors.inputBorder),
            colors = ButtonDefaults.outlinedButtonColors(
                contentColor = colors.navy,
                disabledContentColor = colors.muted
            ),
            contentPadding = PaddingValues(horizontal = 16.dp)
        ) {
            Text(
                text = action.label,
                fontSize = AppDialogButtonTextSize,
                fontWeight = FontWeight.Medium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
internal fun AppDialogFieldColumn(
    label: String,
    modifier: Modifier = Modifier,
    required: Boolean = false,
    helperText: String = "",
    error: String = "",
    colors: AppDialogColors = AppDialogDefaultColors,
    labelFontSize: TextUnit = AppDialogLabelSize,
    contentSpacing: Dp = AppDialogFieldSpacing,
    field: @Composable () -> Unit
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(contentSpacing)) {
        Row(horizontalArrangement = Arrangement.spacedBy(4.dp), verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = label,
                color = colors.navy,
                fontSize = labelFontSize,
                fontWeight = FontWeight.SemiBold
            )
            if (required) {
                Text(
                    text = "*",
                    color = colors.danger,
                    fontSize = labelFontSize,
                    fontWeight = FontWeight.SemiBold
                )
            }
            if (helperText.isNotBlank()) {
                Text(
                    text = "($helperText)",
                    color = colors.muted,
                    fontSize = AppDialogHelperSize,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
        field()
        if (error.isNotBlank()) {
            Text(
                text = error,
                color = colors.danger,
                fontSize = AppDialogHelperSize,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

/**
 * Standard labeled text input for add, edit, and filter forms. This is the
 * preferred one-call field API for new dialogs: label, required marker, error
 * text, field geometry, input typography, and colors all come from the shared
 * dialog system.
 */
@Composable
internal fun AppDialogLabeledTextField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier,
    required: Boolean = false,
    error: String = "",
    colors: AppDialogColors = AppDialogDefaultColors,
    keyboardType: KeyboardType = KeyboardType.Text,
    maxLength: Int? = null,
    leadingIcon: (@Composable () -> Unit)? = null,
    trailingIcon: (@Composable () -> Unit)? = null,
    onFocusLost: (() -> Unit)? = null,
    leadingIconStartPadding: Dp = AppDialogInputHorizontalPadding
) {
    AppDialogFieldColumn(
        label = label,
        required = required,
        error = error,
        colors = colors,
        modifier = modifier
    ) {
        AppDialogTextField(
            value = value,
            onValueChange = onValueChange,
            placeholder = placeholder,
            colors = colors,
            error = error,
            keyboardType = keyboardType,
            maxLength = maxLength,
            leadingIcon = leadingIcon,
            trailingIcon = trailingIcon,
            onFocusLost = onFocusLost
        )
    }
}

/** Shared horizontal field group used by wide dialog forms and filters. */
@Composable
internal fun AppDialogFieldRow(
    modifier: Modifier = Modifier,
    spacing: Dp = 24.dp,
    content: @Composable RowScope.() -> Unit
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(spacing),
        content = content
    )
}

/** Responsive two-column form layout with an explicit compact field order. */
@Composable
internal fun AppDialogResponsiveFieldColumns(
    compact: Boolean,
    modifier: Modifier = Modifier,
    compactSpacing: Dp = 18.dp,
    columnSpacing: Dp = 22.dp,
    rowSpacing: Dp = 28.dp,
    compactContent: @Composable ColumnScope.() -> Unit,
    firstColumn: @Composable ColumnScope.() -> Unit,
    secondColumn: @Composable ColumnScope.() -> Unit
) {
    if (compact) {
        Column(
            modifier = modifier,
            verticalArrangement = Arrangement.spacedBy(compactSpacing),
            content = compactContent
        )
    } else {
        AppDialogFieldRow(modifier = modifier, spacing = rowSpacing) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(columnSpacing),
                content = firstColumn
            )
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(columnSpacing),
                content = secondColumn
            )
        }
    }
}

/** Responsive scrollable layout host shared by add/edit dialog forms. */
@Composable
internal fun AppDialogFormGrid(
    modifier: Modifier = Modifier,
    compactBreakpoint: Dp = 820.dp,
    contentAlignment: Alignment = Alignment.TopStart,
    content: @Composable (compact: Boolean) -> Unit
) {
    BoxWithConstraints(modifier = modifier, contentAlignment = contentAlignment) {
        content(maxWidth < compactBreakpoint)
    }
}

@Composable
internal fun AppDialogSectionLabel(
    text: String,
    modifier: Modifier = Modifier,
    colors: AppDialogColors = AppDialogDefaultColors,
    fontSize: TextUnit = AppDialogSectionTitleSize
) {
    Text(
        text = text,
        modifier = modifier,
        color = colors.navy,
        fontSize = fontSize,
        fontWeight = FontWeight.SemiBold
    )
}

@Composable
internal fun AppDialogTextField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier,
    colors: AppDialogColors = AppDialogDefaultColors,
    error: String = "",
    keyboardType: KeyboardType = KeyboardType.Text,
    singleLine: Boolean = true,
    fieldHeight: Dp = AppDialogFieldHeight,
    fontSize: TextUnit = AppDialogInputSize,
    maxLength: Int? = null,
    readOnly: Boolean = false,
    leadingIcon: (@Composable () -> Unit)? = null,
    trailingIcon: (@Composable () -> Unit)? = null,
    onFocusLost: (() -> Unit)? = null,
    leadingIconStartPadding: Dp = AppDialogInputHorizontalPadding
) {
    var focused by remember { mutableStateOf(false) }
    var hadFocus by remember { mutableStateOf(false) }
    // Read-only fields stay visually muted and must not take focus / primary border.
    val borderColor = when {
        error.isNotBlank() -> colors.danger
        focused && !readOnly -> colors.primary
        else -> colors.inputBorder
    }

    val isMultiline = !singleLine
    val contentAlignment = if (isMultiline) Alignment.TopStart else Alignment.CenterStart
    val rowVerticalAlignment = if (isMultiline) Alignment.Top else Alignment.CenterVertically
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .height(fieldHeight),
        shape = AppDialogFieldShape,
        color = if (readOnly) AppSecondaryBackground else colors.surface,
        border = BorderStroke(1.dp, borderColor)
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(
                    start = leadingIconStartPadding,
                    end = AppDialogInputHorizontalPadding,
                    top = if (isMultiline) 10.dp else 0.dp,
                    bottom = if (isMultiline) 10.dp else 0.dp
                ),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = rowVerticalAlignment
        ) {
            leadingIcon?.invoke()
            Box(modifier = Modifier.weight(1f).fillMaxHeight(), contentAlignment = contentAlignment) {
                BasicTextField(
                    value = value,
                    onValueChange = { newValue ->
                        onValueChange(maxLength?.let { newValue.take(it) } ?: newValue)
                    },
                    readOnly = readOnly,
                    singleLine = singleLine,
                    textStyle = TextStyle(
                        fontSize = fontSize,
                        fontWeight = FontWeight.Normal,
                        color = colors.navy
                    ),
                    keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
                    cursorBrush = SolidColor(if (readOnly) Color.Transparent else colors.primary),
                    modifier = Modifier
                        .fillMaxWidth()
                        .focusProperties { canFocus = !readOnly }
                        .onFocusChanged {
                            focused = it.isFocused && !readOnly
                            if (hadFocus && !it.isFocused) onFocusLost?.invoke()
                            hadFocus = it.isFocused
                        }
                ) { innerTextField ->
                    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = contentAlignment) {
                        if (value.isBlank()) {
                            Text(
                                text = placeholder,
                                color = colors.muted,
                                fontSize = fontSize,
                                fontWeight = FontWeight.Normal,
                                maxLines = if (isMultiline) Int.MAX_VALUE else 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                        innerTextField()
                    }
                }
            }
            trailingIcon?.invoke()
        }
    }
}

/** Numeric text field with shared increment/decrement affordances. */
@Composable
internal fun AppDialogNumberField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier,
    colors: AppDialogColors = AppDialogDefaultColors,
    fieldHeight: Dp = AppDialogFieldHeight,
    minValue: Int = 0,
    maxValue: Int = 999
) {
    fun changeBy(delta: Int) {
        val current = value.toIntOrNull() ?: 0
        onValueChange((current + delta).coerceIn(minValue, maxValue).toString())
    }
    AppDialogTextField(
        value = value,
        onValueChange = { onValueChange(it.filter(Char::isDigit).take(3)) },
        placeholder = placeholder,
        modifier = modifier,
        colors = colors,
        keyboardType = KeyboardType.Number,
        fieldHeight = fieldHeight,
        trailingIcon = {
            Column(modifier = Modifier.width(22.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                Icon(painterResource(Res.drawable.chevron_up), "Increase", Modifier.size(14.dp).clickable { changeBy(1) }, tint = colors.navy)
                Icon(painterResource(Res.drawable.chevron_down), "Decrease", Modifier.size(14.dp).clickable { changeBy(-1) }, tint = colors.navy)
            }
        }
    )
}

@Composable
internal fun AppDialogFullNameField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "Enter full name",
    error: String = "",
    colors: AppDialogColors = AppDialogDefaultColors
) {
    AppDialogTextField(
        value = value,
        onValueChange = onValueChange,
        placeholder = placeholder,
        modifier = modifier,
        error = error,
        colors = colors,
        keyboardType = KeyboardType.Text
    )
}

@Composable
internal fun AppDialogMobileField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "Enter mobile number",
    error: String = "",
    colors: AppDialogColors = AppDialogDefaultColors,
    maxDigits: Int = 10
) {
    AppDialogTextField(
        value = value,
        onValueChange = { onValueChange(it.filter(Char::isDigit).take(maxDigits)) },
        placeholder = placeholder,
        modifier = modifier,
        error = error,
        colors = colors,
        keyboardType = KeyboardType.Phone
    )
}

@Composable
internal fun AppDialogDateField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "DD MMM YYYY",
    error: String = "",
    colors: AppDialogColors = AppDialogDefaultColors,
    fieldHeight: Dp = AppDialogFieldHeight,
    fontSize: TextUnit = 14.sp,
    readOnly: Boolean = false,
    leadingIconStartPadding: Dp = AppDialogInputHorizontalPadding,
    weekSelection: Boolean = false,
    minimumDate: String? = null
) {
    var showCalendarPicker by remember { mutableStateOf(false) }
    var dateInputError by remember { mutableStateOf("") }


    fun commitTypedDate() {
        val trimmed = value.trim()
        if (trimmed.isBlank()) {
            dateInputError = ""
            return
        }
        val normalized = normalizeAppDateInput(trimmed)
        if (normalized == null) {
            dateInputError = "Enter a valid date, for example 24 Feb 1989."
        } else if (minimumDate != null && isAppDateBefore(normalized, minimumDate)) {
            dateInputError = "Date cannot be earlier than $minimumDate."
        } else {
            dateInputError = ""
            if (normalized != value) onValueChange(normalized)
        }
    }

    AppDialogTextField(
        value = value,
        onValueChange = {
            dateInputError = ""
            onValueChange(it)
        },
        placeholder = placeholder,
        modifier = modifier,
        onFocusLost = { commitTypedDate() },
        error = dateInputError.ifBlank { error },
        colors = colors,
        keyboardType = KeyboardType.Text,
        fieldHeight = fieldHeight,
        fontSize = fontSize,
        readOnly = readOnly,
        leadingIconStartPadding = leadingIconStartPadding,
        leadingIcon = {
            Box(
                modifier = Modifier
                    .size(32.dp)
                    .background(colors.iconTile, RoundedCornerShape(10.dp))
                    .then(
                        if (readOnly && !weekSelection) Modifier else Modifier.clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { showCalendarPicker = true }
                        )
                    ),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = painterResource(Res.drawable.calendar),
                    contentDescription = "Open calendar",
                    tint = colors.primary,
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    )

    if (showCalendarPicker && (!readOnly || weekSelection)) {
        AppDialogDatePickerDialog(
            value = value,
            onDismiss = { showCalendarPicker = false },
            onDateSelected = {
                onValueChange(it)
                showCalendarPicker = false
            },
            colors = colors,
            weekSelection = weekSelection,
            minimumDate = minimumDate
        )
    }
}

/** Shared read-only time field with the same themed picker treatment as AppDialogDateField. */
@Composable
internal fun AppDialogTimeField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "HH:MM",
    error: String = "",
    colors: AppDialogColors = AppDialogDefaultColors,
    fieldHeight: Dp = AppDialogFieldHeight,
    leadingIconStartPadding: Dp = AppDialogInputHorizontalPadding
) {
    var showTimePicker by remember { mutableStateOf(false) }
    var timeInputError by remember { mutableStateOf("") }

    fun commitTypedTime() {
        val trimmed = value.trim()
        if (trimmed.isBlank()) {
            timeInputError = ""
            return
        }
        val normalized = normalizeAppTime(trimmed)
        if (normalized == null) {
            timeInputError = "Enter a valid time, for example 14:30."
        } else {
            timeInputError = ""
            if (normalized != value) onValueChange(normalized)
        }
    }

    AppDialogTextField(
        value = value,
        onValueChange = {
            timeInputError = ""
            onValueChange(it)
        },
        placeholder = placeholder,
        modifier = modifier,
        onFocusLost = { commitTypedTime() },
        colors = colors,
        error = timeInputError.ifBlank { error },
        fieldHeight = fieldHeight,
        keyboardType = KeyboardType.Ascii,
        leadingIconStartPadding = leadingIconStartPadding,
        leadingIcon = {
            Box(
                modifier = Modifier
                    .size(32.dp)
                    .background(colors.iconTile, RoundedCornerShape(10.dp))
                    .clickable { showTimePicker = true },
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = painterResource(Res.drawable.clock),
                    contentDescription = "Open time picker",
                    tint = colors.primary,
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    )

    if (showTimePicker) {
        AppDialogTimePickerDialog(
            value = value,
            onDismiss = { showTimePicker = false },
            onTimeSelected = {
                onValueChange(it)
                showTimePicker = false
            },
            colors = colors
        )
    }
}

@Composable
internal fun AppDialogTimePickerDialog(
    value: String,
    onDismiss: () -> Unit,
    onTimeSelected: (String) -> Unit,
    colors: AppDialogColors = AppDialogDefaultColors
) {
    val initial = remember(value) { parseAppTime(value) ?: AppTimeParts(9, 0) }
    var selectedHour by remember(value) { mutableStateOf(initial.hour) }
    var selectedMinute by remember(value) { mutableStateOf(initial.minute) }
    var selectedMeridiem by remember(value) { mutableStateOf(if (initial.hour >= 12) "PM" else "AM") }
    var selectingMinutes by remember(value) { mutableStateOf(false) }

    AppDialogScaffold(
        onDismiss = onDismiss,
        colors = colors,
        size = AppDialogSize.Compact,
        dialogWidthOverride = 480.dp,
        maxDialogHeight = 680.dp
    ) {
        Column(Modifier.fillMaxWidth()) {
            AppDialogHeader(
                title = "Select Time",
                subtitle = "Choose a time.",
                onClose = onDismiss,
                icon = Res.drawable.clock,
                colors = colors
            )
            AppDialogDivider(colors)
            Column(
                Modifier.fillMaxWidth().padding(horizontal = 20.dp, vertical = 18.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center, verticalAlignment = Alignment.CenterVertically) {
                    TimePickerValueBox(formatAppTime(selectedHour, 0, selectedMeridiem).substringBefore(":"), selectingMinutes = false, colors = colors) { selectingMinutes = false }
                    Text(":", color = colors.navy, fontSize = 30.sp, fontWeight = FontWeight.ExtraBold)
                    TimePickerValueBox(selectedMinute.toString().padStart(2, '0'), selectingMinutes = selectingMinutes, colors = colors) { selectingMinutes = true }
                    Column(Modifier.padding(start = 10.dp)) {
                        listOf("AM", "PM").forEach { meridiem ->
                            val selected = meridiem == selectedMeridiem
                            Surface(
                                modifier = Modifier.width(66.dp).height(38.dp).clickable { selectedMeridiem = meridiem; selectedHour = to24Hour(displayHour(selectedHour), meridiem) },
                                shape = RoundedCornerShape(6.dp),
                                color = if (selected) colors.iconTile else colors.surface,
                                border = BorderStroke(1.dp, if (selected) colors.primary else colors.border)
                            ) { Box(contentAlignment = Alignment.Center) { Text(meridiem, color = if (selected) colors.primary else colors.navy, fontSize = 14.sp, fontWeight = FontWeight.Bold) } }
                        }
                    }
                }
                TimePickerClockFace(
                    selectingMinutes = selectingMinutes,
                    selectedHour = displayHour(selectedHour),
                    selectedMinute = selectedMinute,
                    colors = colors,
                    onHourSelected = { selectedHour = to24Hour(it, selectedMeridiem); selectingMinutes = true },
                    onMinuteSelected = { selectedMinute = it }
                )
            }
            AppDialogDivider(colors)
            AppDialogFooter(
                actions = buildList {
                    if (value.isNotBlank()) add(AppDialogFooterAction("Clear", { onTimeSelected(""); onDismiss() }))
                    add(AppDialogFooterAction("Cancel", onDismiss))
                    add(AppDialogFooterAction("Select", { onTimeSelected(formatAppTime(selectedHour, selectedMinute, selectedMeridiem)); onDismiss() }, style = AppDialogActionStyle.Primary))
                },
                colors = colors,
                equalWeightButtons = true
            )
        }
    }
}

@Composable
private fun TimePickerValueBox(value: String, selectingMinutes: Boolean, colors: AppDialogColors, onClick: () -> Unit) {
    Surface(
        modifier = Modifier.width(92.dp).height(70.dp).clickable(onClick = onClick),
        shape = RoundedCornerShape(8.dp),
        color = if (selectingMinutes) colors.iconTile else colors.surface,
        border = BorderStroke(1.dp, if (selectingMinutes) colors.primary else colors.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            Text(value, color = if (selectingMinutes) colors.primary else colors.navy, fontSize = 38.sp, fontWeight = FontWeight.Medium)
        }
    }
}

@Composable
private fun TimePickerClockFace(
    selectingMinutes: Boolean,
    selectedHour: Int,
    selectedMinute: Int,
    colors: AppDialogColors,
    onHourSelected: (Int) -> Unit,
    onMinuteSelected: (Int) -> Unit
) {
    val options = if (selectingMinutes) (0..55 step 5).toList() else (1..12).toList()
    val selected = if (selectingMinutes) selectedMinute else selectedHour
    Box(Modifier.fillMaxWidth().height(300.dp), contentAlignment = Alignment.Center) {
        Box(Modifier.size(280.dp), contentAlignment = Alignment.Center) {
            Canvas(Modifier.matchParentSize()) {
            val center = Offset(size.width / 2f, size.height / 2f)
            val radius = size.minDimension * .36f
            val selectedIndex = options.indexOf(selected).coerceAtLeast(0)
            val angle = (selectedIndex * 30f - 90f) * (PI / 180f).toFloat()
            val point = Offset(center.x + cos(angle) * radius, center.y + sin(angle) * radius)
            drawCircle(colors.border.copy(alpha = .34f), radius = size.minDimension * .49f, center = center)
            drawLine(colors.primary, center, point, strokeWidth = 3.dp.toPx())
            drawCircle(colors.primary, radius = 6.dp.toPx(), center = center)
            drawCircle(colors.primary, radius = 25.dp.toPx(), center = point)
            }
            options.forEachIndexed { index, option ->
            val angle = (index * 30f - 90f) * (PI / 180f).toFloat()
            Box(
                Modifier
                    .offset(x = (cos(angle) * 100f).dp, y = (sin(angle) * 100f).dp)
                    .size(50.dp)
                    .clickable { if (selectingMinutes) onMinuteSelected(option) else onHourSelected(option) },
                contentAlignment = Alignment.Center
            ) {
                Text(option.toString().padStart(2, '0'), color = if (option == selected) Color.White else colors.navy, fontSize = 15.sp, fontWeight = if (option == selected) FontWeight.Bold else FontWeight.Medium)
            }
            }
        }
    }
}

@Composable
private fun <T> TimePickerOptionGrid(
    title: String,
    options: List<T>,
    selected: T,
    optionLabel: (T) -> String,
    onSelected: (T) -> Unit,
    colors: AppDialogColors,
    modifier: Modifier = Modifier
) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(title, color = colors.navy, fontSize = 14.sp, fontWeight = FontWeight.ExtraBold)
        LazyVerticalGrid(
            columns = GridCells.Fixed(4),
            modifier = Modifier.fillMaxWidth().height(220.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            items(options) { option ->
                val isSelected = option == selected
                Surface(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(42.dp)
                        .clickable { onSelected(option) },
                    shape = RoundedCornerShape(9.dp),
                    color = if (isSelected) colors.primary else colors.surface,
                    border = BorderStroke(1.dp, if (isSelected) colors.primary else colors.border)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Text(optionLabel(option), color = if (isSelected) Color.White else colors.navy, fontSize = 14.sp, fontWeight = if (isSelected) FontWeight.ExtraBold else FontWeight.Medium)
                    }
                }
            }
        }
    }
}

private data class AppTimeParts(val hour: Int, val minute: Int)

private fun parseAppTime(value: String): AppTimeParts? {
    val trimmed = value.trim().uppercase().replace(".", "")
    val hasMeridiem = trimmed.endsWith("AM") || trimmed.endsWith("PM")
    val meridiem = if (trimmed.endsWith("PM")) "PM" else "AM"
    val time = if (hasMeridiem) trimmed.dropLast(2).trim() else trimmed
    val compact = time.filter(Char::isDigit)
    val normalizedTime = when {
        time.contains(":") -> time
        compact.length <= 2 -> "$compact:00"
        compact.length == 3 -> "${compact.dropLast(2)}:${compact.takeLast(2)}"
        compact.length == 4 -> "${compact.take(2)}:${compact.takeLast(2)}"
        else -> return null
    }
    val parts = normalizedTime.split(":")
    if (parts.size != 2) return null
    val enteredHour = parts[0].toIntOrNull() ?: return null
    val minute = parts[1].toIntOrNull() ?: return null
    val hour = if (hasMeridiem) to24Hour(enteredHour, meridiem) else enteredHour
    return AppTimeParts(hour, minute).takeIf { enteredHour in (if (hasMeridiem) 1..12 else 0..23) && it.minute in 0..59 }
}

private fun formatAppTime(hour: Int, minute: Int, meridiem: String = if (hour >= 12) "PM" else "AM"): String {
    return "${displayHour(hour).toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} $meridiem"
}

private fun displayHour(hour: Int): Int = when (hour % 12) { 0 -> 12; else -> hour % 12 }

private fun to24Hour(hour: Int, meridiem: String): Int {
    val normalized = hour % 12
    return if (meridiem == "PM") normalized + 12 else normalized
}

private fun normalizeAppTime(value: String): String? {
    val parsed = parseAppTime(value) ?: return null
    val meridiem = if (parsed.hour >= 12) "PM" else "AM"
    return formatAppTime(parsed.hour, parsed.minute, meridiem)
}

@Composable
internal fun AppDialogDatePickerDialog(
    value: String,
    onDismiss: () -> Unit,
    onDateSelected: (String) -> Unit,
    colors: AppDialogColors = AppDialogDefaultColors,
    yearRange: IntRange = 1900..2100,
    weekSelection: Boolean = false,
    weekRangeSelection: Boolean = false,
    weekRangeStartValue: String = "",
    weekRangeEndValue: String = "",
    onWeekRangeSelected: ((String, String) -> Unit)? = null,
    dateRangeSelection: Boolean = false,
    dateRangeStartValue: String = "",
    dateRangeEndValue: String = "",
    onDateRangeSelected: ((String, String) -> Unit)? = null,
    minimumDate: String? = null
) {
    val minimumParts = remember(minimumDate, yearRange) {
        minimumDate?.toDatePartsOrNull()?.coerceYear(yearRange)
    }
    val seedParts = remember(value, yearRange, minimumParts) {
        value.toDatePartsOrNull()?.coerceYear(yearRange)
            ?.takeUnless { minimumParts != null && datePartsToDays(it.year, it.month, it.day) < datePartsToDays(minimumParts.year, minimumParts.month, minimumParts.day) }
            ?: minimumParts
            ?: currentUtcDateParts().coerceYear(yearRange)
    }
    var selectedParts by remember(value, yearRange) { mutableStateOf(seedParts) }
    var selectedRangeStart by remember(value, weekRangeStartValue, dateRangeStartValue, yearRange) {
        mutableStateOf(
            when {
                dateRangeSelection -> dateRangeStartValue.toDatePartsOrNull() ?: seedParts
                else -> (weekRangeStartValue.toDatePartsOrNull() ?: seedParts).toMondayStart()
            }
        )
    }
    var selectedRangeEnd by remember(value, weekRangeStartValue, weekRangeEndValue, dateRangeStartValue, dateRangeEndValue, yearRange) {
        val start = if (dateRangeSelection) {
            dateRangeStartValue.toDatePartsOrNull() ?: seedParts
        } else {
            (weekRangeStartValue.toDatePartsOrNull() ?: seedParts).toMondayStart()
        }
        mutableStateOf(
            if (dateRangeSelection) dateRangeEndValue.toDatePartsOrNull() ?: start
            else (weekRangeEndValue.toDatePartsOrNull() ?: start).toMondayStart()
        )
    }
    var isChoosingRangeEnd by remember(value, weekRangeStartValue, weekRangeEndValue, dateRangeStartValue, dateRangeEndValue) {
        mutableStateOf(dateRangeSelection && dateRangeStartValue.isNotBlank() && dateRangeEndValue.isBlank())
    }
    var hasCompletedRangeSelection by remember(value, weekRangeStartValue, weekRangeEndValue, dateRangeStartValue, dateRangeEndValue) {
        mutableStateOf(
            if (dateRangeSelection) dateRangeStartValue.isNotBlank() && dateRangeEndValue.isNotBlank()
            else weekRangeStartValue.isNotBlank() && weekRangeEndValue.isNotBlank()
        )
    }
    var hasSelectedDateRangeStart by remember(value, dateRangeStartValue, dateRangeEndValue) {
        mutableStateOf(dateRangeStartValue.isNotBlank())
    }
    var hoveredDateRangeDayNumber by remember(value, dateRangeStartValue, dateRangeEndValue) {
        mutableStateOf<Long?>(null)
    }
    var isRangeSelectionEmpty by remember(value, weekRangeStartValue, weekRangeEndValue) { mutableStateOf(false) }
    var rangeSelectionHint by remember(value, weekRangeStartValue, weekRangeEndValue) { mutableStateOf("") }
    var displayYear by remember(value, yearRange) { mutableStateOf(seedParts.year) }
    var displayMonth by remember(value, yearRange) { mutableStateOf(seedParts.month) }
    var monthMenuExpanded by remember(value, yearRange) { mutableStateOf(false) }
    var yearMenuExpanded by remember(value, yearRange) { mutableStateOf(false) }
    val today = remember(yearRange) { currentUtcDateParts().coerceYear(yearRange) }
    val calendarMonthCount = (yearRange.last - yearRange.first + 1) * 12
    val initialMonthIndex = ((displayYear - yearRange.first) * 12 + displayMonth - 1).coerceIn(0, calendarMonthCount - 1)
    val calendarListState = rememberLazyListState(initialFirstVisibleItemIndex = initialMonthIndex)
    val calendarScope = rememberCoroutineScope()
    fun navigateToMonth(year: Int, month: Int) {
        if (weekRangeSelection || dateRangeSelection) {
            val index = ((year - yearRange.first) * 12 + month - 1).coerceIn(0, calendarMonthCount - 1)
            calendarScope.launch { calendarListState.animateScrollToItem(index) }
        } else {
            displayYear = year
            displayMonth = month
        }
    }
    LaunchedEffect(weekRangeSelection, dateRangeSelection, calendarListState, yearRange) {
        if (weekRangeSelection || dateRangeSelection) {
            snapshotFlow { calendarListState.firstVisibleItemIndex }.collect { index ->
                displayYear = yearRange.first + index / 12
                displayMonth = index % 12 + 1
            }
        }
    }
    val selectedWeekStartDayNumber = datePartsToDays(selectedParts.year, selectedParts.month, selectedParts.day)
    val rangeStartDayNumber = datePartsToDays(selectedRangeStart.year, selectedRangeStart.month, selectedRangeStart.day)
    val rangeEndStartDayNumber = datePartsToDays(selectedRangeEnd.year, selectedRangeEnd.month, selectedRangeEnd.day)
    val rangeSelectionStartDayNumber = minOf(rangeStartDayNumber, rangeEndStartDayNumber)
    val rangeSelectionEndDayNumber = maxOf(rangeStartDayNumber, rangeEndStartDayNumber) + if (dateRangeSelection) 0L else 6L
    val selectedRangeWeekCount = (maxOf(rangeStartDayNumber, rangeEndStartDayNumber) - rangeSelectionStartDayNumber) / 7L + 1L
    val selectedRangeLabel = if (dateRangeSelection) {
        "${daysToDateParts(minOf(rangeStartDayNumber, rangeEndStartDayNumber)).toReadableLabel()} - ${daysToDateParts(maxOf(rangeStartDayNumber, rangeEndStartDayNumber)).toReadableLabel()}"
    } else if (rangeStartDayNumber <= rangeEndStartDayNumber) {
        "${selectedRangeStart.toReadableLabel()} - ${daysToDateParts(rangeEndStartDayNumber + 6L).toReadableLabel()}"
    } else {
        "${selectedRangeEnd.toReadableLabel()} - ${daysToDateParts(rangeStartDayNumber + 6L).toReadableLabel()}"
    }
    val selectedWeekEndDayNumber = selectedWeekStartDayNumber + 6L
    val calendarDays = remember(displayYear, displayMonth) { monthGrid(displayYear, displayMonth) }
    val canGoPrevious = (displayYear > yearRange.first || (displayYear == yearRange.first && displayMonth > 1)) &&
        (minimumParts == null || displayYear > minimumParts.year || (displayYear == minimumParts.year && displayMonth > minimumParts.month))
    val canGoNext = displayYear < yearRange.last || (displayYear == yearRange.last && displayMonth < 12)
    val monthOptions = remember { (1..12).map { it to monthName(it) } }
    val yearOptions = remember(yearRange) { (yearRange.first..yearRange.last).toList() }
    val onCalendarDayClick: (AppDateParts) -> Unit = { pickedDate ->
        if (weekRangeSelection) {
            val pickedWeek = pickedDate.toMondayStart()
            val pickedWeekDayNumber = datePartsToDays(pickedWeek.year, pickedWeek.month, pickedWeek.day)
            val isInsideCompletedRange = hasCompletedRangeSelection && !isRangeSelectionEmpty &&
                pickedWeekDayNumber in rangeSelectionStartDayNumber..rangeSelectionEndDayNumber
            rangeSelectionHint = ""
            if (isInsideCompletedRange) {
                when {
                    selectedRangeWeekCount == 1L -> {
                        isRangeSelectionEmpty = true
                        hasCompletedRangeSelection = false
                    }
                    pickedWeekDayNumber == rangeSelectionStartDayNumber -> {
                        selectedRangeStart = daysToDateParts(rangeSelectionStartDayNumber + 7L)
                        selectedRangeEnd = daysToDateParts(rangeSelectionEndDayNumber - 6L)
                    }
                    pickedWeekDayNumber == rangeSelectionEndDayNumber - 6L -> {
                        selectedRangeStart = daysToDateParts(rangeSelectionStartDayNumber)
                        selectedRangeEnd = daysToDateParts(rangeSelectionEndDayNumber - 13L)
                    }
                    else -> rangeSelectionHint = "Weeks must stay consecutive. Remove a week from either end."
                }
            } else if (!isChoosingRangeEnd || isRangeSelectionEmpty) {
                selectedRangeStart = pickedWeek
                selectedRangeEnd = pickedWeek
                isChoosingRangeEnd = true
                hasCompletedRangeSelection = false
                isRangeSelectionEmpty = false
            } else {
                selectedRangeEnd = pickedWeek
                isChoosingRangeEnd = false
                hasCompletedRangeSelection = true
            }
        } else if (dateRangeSelection) {
            if (!isChoosingRangeEnd || hasCompletedRangeSelection) {
                selectedRangeStart = pickedDate
                selectedRangeEnd = pickedDate
                isChoosingRangeEnd = true
                hasCompletedRangeSelection = false
                hasSelectedDateRangeStart = true
            } else {
                selectedRangeEnd = pickedDate
                isChoosingRangeEnd = false
                hasCompletedRangeSelection = true
            }
        } else {
            selectedParts = if (weekSelection) pickedDate.toMondayStart() else pickedDate
        }
    }
    val onCalendarDayDoubleClick: (AppDateParts) -> Unit = { pickedDate ->
        val selectedDate = if (weekSelection) pickedDate.toMondayStart() else pickedDate
        if (weekRangeSelection) {
            selectedRangeStart = selectedDate
            selectedRangeEnd = selectedDate
            isChoosingRangeEnd = false
            hasCompletedRangeSelection = true
            isRangeSelectionEmpty = false
            rangeSelectionHint = ""
            onWeekRangeSelected?.invoke(selectedDate.toReadableLabel(), selectedDate.toReadableLabel())
            onDismiss()
        } else if (dateRangeSelection) {
            selectedRangeStart = pickedDate
            selectedRangeEnd = pickedDate
            isChoosingRangeEnd = false
            hasCompletedRangeSelection = true
            hasSelectedDateRangeStart = true
            onDateRangeSelected?.invoke(pickedDate.toReadableLabel(), pickedDate.toReadableLabel())
            onDismiss()
        } else {
            selectedParts = selectedDate
            onDateSelected(selectedDate.toReadableLabel())
            onDismiss()
        }
    }

    AppDialogScaffold(
        onDismiss = onDismiss,
        colors = colors,
        size = AppDialogSize.Compact,
        dialogWidthOverride = if (weekRangeSelection || dateRangeSelection) 480.dp else null,
        maxDialogHeight = if (weekRangeSelection || dateRangeSelection) 840.dp else 680.dp,
        fillDialogHeight = weekRangeSelection || dateRangeSelection
    ) {
        Column(
            modifier = if (weekRangeSelection || dateRangeSelection) Modifier.fillMaxSize() else Modifier.fillMaxWidth()
        ) {
            AppDialogHeader(
                title = when {
                    dateRangeSelection -> "Select Date Range"
                    weekRangeSelection -> "Select Weeks"
                    weekSelection -> "Select Week"
                    else -> "Select Date"
                },
                subtitle = when {
                    dateRangeSelection && !hasSelectedDateRangeStart -> "Choose a start date. Hover to preview."
                    dateRangeSelection && isChoosingRangeEnd -> "Start: ${selectedRangeStart.toReadableLabel()}. Hover, then click the end date."
                    dateRangeSelection && hasCompletedRangeSelection -> selectedRangeLabel
                    dateRangeSelection -> "Choose a start date, then an end date."
                    weekRangeSelection && isRangeSelectionEmpty -> "No week selected. Choose a start week."
                    weekRangeSelection && rangeSelectionHint.isNotBlank() -> rangeSelectionHint
                    weekRangeSelection && isChoosingRangeEnd -> "Start: ${selectedRangeStart.toReadableLabel()}. Choose an end week."
                    weekRangeSelection && hasCompletedRangeSelection -> selectedRangeLabel
                    weekRangeSelection -> "Choose a start week, then an end week."
                    weekSelection -> "Choose any day in the week you want to view."
                    else -> "Choose a date from the calendar."
                },
                onClose = onDismiss,
                icon = Res.drawable.calendar,
                colors = colors
            )
            AppDialogDivider(colors = colors)
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .then(if (weekRangeSelection || dateRangeSelection) Modifier.weight(1f) else Modifier)
                    .padding(horizontal = 18.dp, vertical = 16.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    CalendarNavButton(
                        label = "‹",
                        enabled = canGoPrevious,
                        colors = colors,
                        onClick = {
                            val prev = previousMonth(displayYear, displayMonth)
                            navigateToMonth(prev.first, prev.second)
                        }
                    )
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                        CalendarPickerDropdownButton(
                            text = monthName(displayMonth),
                            expanded = monthMenuExpanded,
                            colors = colors,
                            options = monthOptions,
                            selectedLabel = monthName(displayMonth),
                            width = 96.dp,
                            gridColumns = 3,
                            popupWidthOverride = 224.dp,
                            onToggle = {
                                monthMenuExpanded = !monthMenuExpanded
                                yearMenuExpanded = false
                            },
                            onSelected = { selected ->
                                navigateToMonth(displayYear, selected.first)
                                monthMenuExpanded = false
                            },
                            onDismiss = { monthMenuExpanded = false },
                            optionLabel = { it.second }
                        )
                        CalendarPickerDropdownButton(
                            text = displayYear.toString(),
                            expanded = yearMenuExpanded,
                            colors = colors,
                            options = yearOptions.map { it to it.toString() },
                            selectedLabel = displayYear.toString(),
                            width = 82.dp,
                            gridColumns = 3,
                            popupWidthOverride = 224.dp,
                            onToggle = {
                                yearMenuExpanded = !yearMenuExpanded
                                monthMenuExpanded = false
                            },
                            onSelected = { selected ->
                                navigateToMonth(selected.first, displayMonth)
                                yearMenuExpanded = false
                            },
                            onDismiss = { yearMenuExpanded = false },
                            optionLabel = { it.second }
                        )
                    }
                    CalendarNavButton(
                        label = "›",
                        enabled = canGoNext,
                        colors = colors,
                        onClick = {
                            val next = nextMonth(displayYear, displayMonth)
                            navigateToMonth(next.first, next.second)
                        }
                    )
                }

                if (weekRangeSelection || dateRangeSelection) {
                    LazyColumn(
                        state = calendarListState,
                        modifier = Modifier.fillMaxWidth().weight(1f),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        items(calendarMonthCount) { monthIndex ->
                            val monthYear = yearRange.first + monthIndex / 12
                            val monthNumber = monthIndex % 12 + 1
                            val monthDays = remember(monthYear, monthNumber) { monthGrid(monthYear, monthNumber, showAdjacentMonthDays = false) }
                            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                                Text(
                                    text = "${monthName(monthNumber)} $monthYear",
                                    color = colors.navy,
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.SemiBold
                                )
                                CalendarWeekdayHeader(colors)
                                CalendarMonthGrid(
                                    calendarDays = monthDays,
                                    today = today,
                                    yearRange = yearRange,
                                    colors = colors,
                                    weekSelection = weekRangeSelection,
                                    weekRangeSelection = weekRangeSelection,
                                    dateRangeSelection = dateRangeSelection,
                                    selectedParts = selectedParts,
                                    selectedWeekStartDayNumber = selectedWeekStartDayNumber,
                                    selectedWeekEndDayNumber = selectedWeekEndDayNumber,
                                    rangeStartDayNumber = rangeStartDayNumber,
                                    rangeEndStartDayNumber = rangeEndStartDayNumber,
                                    rangeSelectionStartDayNumber = rangeSelectionStartDayNumber,
                                    rangeSelectionEndDayNumber = rangeSelectionEndDayNumber,
                                    isChoosingRangeEnd = isChoosingRangeEnd,
                                    isRangeSelectionEmpty = isRangeSelectionEmpty,
                                    hasCompletedRangeSelection = hasCompletedRangeSelection,
                                    hasSelectedDateRangeStart = hasSelectedDateRangeStart,
                                    hoveredDateRangeDayNumber = hoveredDateRangeDayNumber,
                                    onDayHover = { hoveredDateRangeDayNumber = it },
                                    minimumDayNumber = minimumParts?.let { datePartsToDays(it.year, it.month, it.day) },
                                    onDayClick = onCalendarDayClick,
                                    onDayDoubleClick = onCalendarDayDoubleClick
                                )
                            }
                        }
                    }
                } else {
                    CalendarWeekdayHeader(colors)
                    CalendarMonthGrid(
                        calendarDays = calendarDays,
                        today = today,
                        yearRange = yearRange,
                        colors = colors,
                        weekSelection = weekSelection,
                        weekRangeSelection = false,
                        dateRangeSelection = dateRangeSelection,
                        selectedParts = selectedParts,
                        selectedWeekStartDayNumber = selectedWeekStartDayNumber,
                        selectedWeekEndDayNumber = selectedWeekEndDayNumber,
                        rangeStartDayNumber = rangeStartDayNumber,
                        rangeEndStartDayNumber = rangeEndStartDayNumber,
                        rangeSelectionStartDayNumber = rangeSelectionStartDayNumber,
                        rangeSelectionEndDayNumber = rangeSelectionEndDayNumber,
                        isChoosingRangeEnd = isChoosingRangeEnd,
                        isRangeSelectionEmpty = isRangeSelectionEmpty,
                        hasCompletedRangeSelection = hasCompletedRangeSelection,
                        hasSelectedDateRangeStart = hasSelectedDateRangeStart,
                        hoveredDateRangeDayNumber = hoveredDateRangeDayNumber,
                        onDayHover = { hoveredDateRangeDayNumber = it },
                        minimumDayNumber = minimumParts?.let { datePartsToDays(it.year, it.month, it.day) },
                        onDayClick = onCalendarDayClick,
                        onDayDoubleClick = onCalendarDayDoubleClick
                    )
                }
            }

            AppDialogDivider(colors = colors)
            AppDialogFooter(
                actions = buildList {
                    if (value.isNotBlank() && !weekSelection && !weekRangeSelection && !dateRangeSelection) {
                        add(
                            AppDialogFooterAction(
                                label = "Clear",
                                onClick = {
                                    onDateSelected("")
                                    onDismiss()
                                },
                                style = AppDialogActionStyle.Secondary
                            )
                        )
                    }
                    add(
                        AppDialogFooterAction(
                            label = "Cancel",
                            onClick = onDismiss,
                            style = AppDialogActionStyle.Secondary
                        )
                    )
                    add(
                            AppDialogFooterAction(
                                label = if (dateRangeSelection) {
                                    when {
                                        hasCompletedRangeSelection -> "Select Range"
                                        hasSelectedDateRangeStart -> "Choose an end date"
                                        else -> "Choose a start date"
                                    }
                                } else if (weekRangeSelection) {
                                    if (isRangeSelectionEmpty) "Choose a week" else "Select $selectedRangeWeekCount ${if (selectedRangeWeekCount == 1L) "week" else "weeks"}"
                                } else if (weekSelection) "Select Week" else "Select",
                                onClick = {
                                    if (dateRangeSelection) {
                                        if (hasCompletedRangeSelection) {
                                            val first = if (rangeStartDayNumber <= rangeEndStartDayNumber) selectedRangeStart else selectedRangeEnd
                                            val last = if (rangeStartDayNumber <= rangeEndStartDayNumber) selectedRangeEnd else selectedRangeStart
                                            onDateRangeSelected?.invoke(first.toReadableLabel(), last.toReadableLabel())
                                        }
                                    } else if (weekRangeSelection) {
                                        if (!isRangeSelectionEmpty) {
                                            val first = if (rangeStartDayNumber <= rangeEndStartDayNumber) selectedRangeStart else selectedRangeEnd
                                            val last = if (rangeStartDayNumber <= rangeEndStartDayNumber) selectedRangeEnd else selectedRangeStart
                                            onWeekRangeSelected?.invoke(first.toReadableLabel(), last.toReadableLabel())
                                        }
                                    } else {
                                        val selected = if (weekSelection) selectedParts.toMondayStart() else selectedParts
                                        onDateSelected(selected.toReadableLabel())
                                    }
                                    onDismiss()
                                },
                            style = AppDialogActionStyle.Primary,
                            enabled = if (dateRangeSelection) hasCompletedRangeSelection else !weekRangeSelection || !isRangeSelectionEmpty
                        )
                    )
                },
                colors = colors,
                equalWeightButtons = true
            )
        }
    }
}

@Composable
private fun CalendarWeekdayHeader(colors: AppDialogColors) {
    Row(modifier = Modifier.fillMaxWidth()) {
        weekLabels().forEach { label ->
            Text(
                text = label,
                modifier = Modifier.weight(1f).fillMaxWidth(),
                color = colors.navy,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
private fun CalendarMonthGrid(
    calendarDays: List<DayCell>,
    today: AppDateParts,
    yearRange: IntRange,
    colors: AppDialogColors,
    weekSelection: Boolean,
    weekRangeSelection: Boolean,
    dateRangeSelection: Boolean,
    selectedParts: AppDateParts,
    selectedWeekStartDayNumber: Long,
    selectedWeekEndDayNumber: Long,
    rangeStartDayNumber: Long,
    rangeEndStartDayNumber: Long,
    rangeSelectionStartDayNumber: Long,
    rangeSelectionEndDayNumber: Long,
    isChoosingRangeEnd: Boolean,
    isRangeSelectionEmpty: Boolean,
    hasCompletedRangeSelection: Boolean,
    hasSelectedDateRangeStart: Boolean,
    hoveredDateRangeDayNumber: Long?,
    minimumDayNumber: Long?,
    onDayClick: (AppDateParts) -> Unit,
    onDayDoubleClick: (AppDateParts) -> Unit,
    onDayHover: (Long?) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(if (weekRangeSelection) 1.dp else 4.dp)) {
        calendarDays.chunked(7).forEach { week ->
            val weekHoverSource = remember(week.firstOrNull()) { MutableInteractionSource() }
            val isWeekHovered by weekHoverSource.collectIsHoveredAsState()
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .then(if (weekRangeSelection) Modifier.hoverable(weekHoverSource) else Modifier)
            ) {
                week.forEach { cell ->
                    val cellDayNumber = datePartsToDays(cell.year, cell.month, cell.day)
                    val cellHoverSource = remember(cell.year, cell.month, cell.day) { MutableInteractionSource() }
                    val isCellHovered by cellHoverSource.collectIsHoveredAsState()
                    LaunchedEffect(isCellHovered) {
                        if (dateRangeSelection && cell.isVisible) {
                            if (isCellHovered) onDayHover(cellDayNumber)
                            else if (hoveredDateRangeDayNumber == cellDayNumber) onDayHover(null)
                        }
                    }
                    val previewEndDayNumber = if (dateRangeSelection && hasSelectedDateRangeStart && isChoosingRangeEnd) {
                        hoveredDateRangeDayNumber ?: rangeEndStartDayNumber
                    } else {
                        rangeEndStartDayNumber
                    }
                    val previewStartDayNumber = minOf(rangeStartDayNumber, previewEndDayNumber)
                    val previewRangeEndDayNumber = maxOf(rangeStartDayNumber, previewEndDayNumber)
                    val isSelectedWeek = when {
                        weekRangeSelection -> cell.isVisible && !isRangeSelectionEmpty && cellDayNumber in rangeSelectionStartDayNumber..rangeSelectionEndDayNumber
                        dateRangeSelection -> cell.isVisible && hasSelectedDateRangeStart && cellDayNumber in previewStartDayNumber..previewRangeEndDayNumber
                        weekSelection -> cell.isVisible && cellDayNumber in selectedWeekStartDayNumber..selectedWeekEndDayNumber
                        else -> false
                    }
                    val isHoveredWeek = weekRangeSelection && isWeekHovered && cell.isVisible
                    val isSelected = if (!cell.isVisible) false else if (dateRangeSelection) {
                        hasSelectedDateRangeStart && (cellDayNumber == minOf(rangeStartDayNumber, previewEndDayNumber) || cellDayNumber == maxOf(rangeStartDayNumber, previewEndDayNumber))
                    } else if (weekRangeSelection) {
                        !isRangeSelectionEmpty && (
                            cellDayNumber == minOf(rangeStartDayNumber, rangeEndStartDayNumber) ||
                                (!isChoosingRangeEnd && cellDayNumber == maxOf(rangeStartDayNumber, rangeEndStartDayNumber) + 6L)
                            )
                    } else if (weekSelection) {
                        cellDayNumber == selectedWeekStartDayNumber
                    } else {
                        selectedParts.year == cell.year && selectedParts.month == cell.month && selectedParts.day == cell.day
                    }
                    val isToday = cell.isVisible && today.year == cell.year && today.month == cell.month && today.day == cell.day
                    val enabled = cell.isVisible && cell.year in yearRange &&
                        (minimumDayNumber == null || cellDayNumber >= minimumDayNumber)
                    val isBeforeMinimum = cell.isVisible && !enabled
                    val background = when {
                        !cell.isVisible -> Color.Transparent
                        isBeforeMinimum -> colors.border.copy(alpha = 0.16f)
                        isSelected -> colors.primary
                        isSelectedWeek -> colors.primary.copy(alpha = 0.12f)
                        isHoveredWeek -> colors.primary.copy(alpha = 0.08f)
                        !cell.inCurrentMonth -> colors.border.copy(alpha = 0.30f)
                        else -> colors.surface
                    }
                    val foreground = when {
                        isBeforeMinimum -> colors.muted.copy(alpha = 0.45f)
                        isSelected -> Color.White
                        isSelectedWeek -> colors.primary
                        isHoveredWeek -> colors.primary
                        !cell.inCurrentMonth -> colors.muted.copy(alpha = 0.55f)
                        else -> colors.navy
                    }
                    val animatedBackground by animateColorAsState(
                        targetValue = background,
                        animationSpec = tween(durationMillis = 150, easing = FastOutSlowInEasing),
                        label = "calendar-day-background"
                    )
                    val animatedForeground by animateColorAsState(
                        targetValue = foreground,
                        animationSpec = tween(durationMillis = 150, easing = FastOutSlowInEasing),
                        label = "calendar-day-foreground"
                    )
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .height(if (weekRangeSelection) 40.dp else 44.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Surface(
                            modifier = Modifier
                                .then(if (weekRangeSelection) Modifier.fillMaxWidth().height(38.dp) else Modifier.size(44.dp))
                                .hoverable(cellHoverSource)
                                .combinedClickable(
                                    enabled = enabled,
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { onDayClick(AppDateParts(cell.year, cell.month, cell.day)) },
                                    onDoubleClick = { onDayDoubleClick(AppDateParts(cell.year, cell.month, cell.day)) }
                                ),
                            shape = if (weekSelection) RoundedCornerShape(12.dp) else CircleShape,
                            color = animatedBackground,
                            border = BorderStroke(
                                width = if (isToday && !isSelected) 1.dp else 0.dp,
                                color = if (isToday && !isSelected) colors.primary else Color.Transparent
                            )
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                Text(
                                    text = if (cell.isVisible) cell.day.toString() else "",
                                    color = animatedForeground,
                                    fontSize = AppDialogInputSize,
                                    fontWeight = if (isSelected || isToday) FontWeight.ExtraBold else FontWeight.Medium
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun AppDateParts.toMondayStart(): AppDateParts {
    val dayNumber = datePartsToDays(year, month, day)
    val mondayFirstOffset = (dayOfWeek(year, month, day) + 6) % 7
    return daysToDateParts(dayNumber - mondayFirstOffset)
}

@Composable
internal fun AppDialogSelectField(
    value: String,
    placeholder: String,
    expanded: Boolean,
    options: List<String>,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelected: (String) -> Unit,
    modifier: Modifier = Modifier,
    colors: AppDialogColors = AppDialogDefaultColors,
    error: String = "",
    emptyLabel: String = "No options available",
    fieldHeight: Dp = AppDialogFieldHeight,
    fontSize: TextUnit = 14.sp
) {
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val selectionScope = rememberCoroutineScope()
    val anchorWidth = with(LocalDensity.current) { anchorSize.width.toDp() }
    val borderColor = when {
        error.isNotBlank() -> colors.danger
        expanded -> colors.primary
        else -> colors.inputBorder
    }
    Box(
        modifier = modifier
            .fillMaxWidth()
            .onGloballyPositioned { anchorSize = it.size }
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .height(fieldHeight)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggleExpanded
                ),
            shape = AppDialogFieldShape,
            color = colors.surface,
            border = BorderStroke(1.dp, borderColor)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                .padding(horizontal = AppDialogInputHorizontalPadding),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = value.ifBlank { placeholder },
                    color = if (value.isBlank()) colors.muted else colors.navy,
                    fontSize = fontSize,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f)
                )
                Icon(
                    painter = painterResource(Res.drawable.chevron_down),
                    contentDescription = null,
                    tint = if (expanded) colors.primary else colors.navy,
                    modifier = Modifier.size(20.dp).rotate(if (expanded) 180f else 0f)
                )
            }
        }

        AppDropdownMenu(
            expanded = expanded,
            onDismissRequest = onDismissExpanded,
            width = anchorWidth.takeIf { it > 0.dp }
        ) {
            if (options.isEmpty()) {
                DropdownMenuItem(
                    text = {
                        Text(
                            text = emptyLabel,
                            color = colors.muted,
                            fontSize = AppDialogInputSize,
                            fontWeight = FontWeight.Medium
                        )
                    },
                    onClick = { selectionScope.launch { delay(16); onDismissExpanded() } }
                )
            } else {
                Column(
                    modifier = Modifier
                        .heightIn(max = 320.dp)
                        .verticalScroll(rememberScrollState())
                ) {
                    options.forEach { option ->
                        DropdownMenuItem(
                            text = {
                                Text(
                                    text = option,
                                    color = colors.navy,
                                    fontSize = AppDialogInputSize,
                                    fontWeight = if (option == value) FontWeight.SemiBold else FontWeight.Medium
                                )
                            },
                            onClick = {
                                // Keep the popup attached until Compose JS finishes
                                // the click interaction and its sound callback.
                                selectionScope.launch {
                                    delay(16)
                                    onSelected(option)
                                    onDismissExpanded()
                                }
                            }
                        )
                    }
                }
            }
        }
    }
}

/** Shared multi-select control for form fields such as classes, staff, and subjects. */
@Composable
internal fun AppDialogMultiSelectField(
    selectedValues: List<String>,
    placeholder: String,
    options: List<String>,
    expanded: Boolean,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelectionChanged: (List<String>) -> Unit,
    modifier: Modifier = Modifier,
    colors: AppDialogColors = AppDialogDefaultColors,
    optionLabels: Map<String, String> = emptyMap(),
    searchPlaceholder: String = "Search classes",
    emptyResultsText: String = "No classes found",
    error: String = "",
    // Chips sit inside the same 40 px standard control height as other fields.
    fieldHeight: Dp = 40.dp
) {
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    var query by remember { mutableStateOf("") }
    val anchorWidth = with(LocalDensity.current) { anchorSize.width.toDp() }
    val filteredOptions = options.filter { option ->
        option.contains(query.trim(), ignoreCase = true) ||
            optionLabels[option].orEmpty().contains(query.trim(), ignoreCase = true)
    }
    Box(modifier = modifier.onGloballyPositioned { anchorSize = it.size }) {
        Surface(
            modifier = Modifier.fillMaxWidth().heightIn(min = fieldHeight).clickable(onClick = onToggleExpanded),
            shape = AppDialogFieldShape,
            color = colors.surface,
            border = BorderStroke(1.dp, when {
                error.isNotBlank() -> colors.danger
                expanded -> colors.primary
                else -> colors.inputBorder
            })
        ) {
            Row(Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                Row(Modifier.weight(1f).horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                    if (selectedValues.isEmpty()) Text(placeholder, color = colors.muted, fontSize = AppDialogInputSize)
                    else selectedValues.forEach { value ->
                        Surface(color = colors.selectedBackground, shape = RoundedCornerShape(6.dp), modifier = Modifier.clickable { onSelectionChanged(selectedValues - value) }) { Text("$value  ×", color = colors.navy, fontSize = 12.sp, fontWeight = FontWeight.Normal, maxLines = 1, modifier = Modifier.padding(horizontal = 9.dp, vertical = 4.dp)) }
                    }
                }
                Icon(painterResource(Res.drawable.chevron_down), null, tint = if (expanded) colors.primary else colors.navy, modifier = Modifier.size(20.dp).rotate(if (expanded) 180f else 0f))
            }
        }
        if (expanded) {
            AppDropdownMenu(expanded = true, onDismissRequest = { query = ""; onDismissExpanded() }, width = anchorWidth.takeIf { it > 0.dp }, containerColor = colors.surface, shape = AppDialogDropdownShape, border = BorderStroke(1.dp, colors.border), shadowElevation = 8.dp) {
                Column(Modifier.widthIn(min = 220.dp).heightIn(max = 320.dp)) {
                    AppDialogTextField(query, { query = it }, searchPlaceholder, Modifier.padding(8.dp), colors = colors, fieldHeight = 40.dp, fontSize = 14.sp)
                    Column(Modifier.fillMaxWidth().verticalScroll(rememberScrollState())) {
                        filteredOptions.forEach { option ->
                            val isSelected = option in selectedValues
                            DropdownMenuItem(text = { Text(optionLabels[option] ?: option, color = colors.navy, fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium) }, onClick = { onSelectionChanged(if (isSelected) selectedValues - option else selectedValues + option) })
                        }
                        if (filteredOptions.isEmpty()) Text(emptyResultsText, color = colors.muted, modifier = Modifier.padding(14.dp))
                    }
                }
            }
        }
    }
}

private data class AppDateParts(
    val year: Int,
    val month: Int,
    val day: Int
) {
    fun coerceYear(yearRange: IntRange): AppDateParts = copy(year = year.coerceIn(yearRange.first, yearRange.last))
    fun toReadableLabel(): String = "${day.toString().padStart(2, '0')} ${monthName(month)} $year"
}

internal fun normalizeAppDateInput(value: String): String? =
    value.toDatePartsOrNull()?.toReadableLabel()

fun currentAppDateLabel(): String = currentUtcDateParts().toReadableLabel()

fun appDateLabelAfterToday(): String = daysToDateParts(
    datePartsToDays(currentUtcDateParts().year, currentUtcDateParts().month, currentUtcDateParts().day) + 1L
).toReadableLabel()

fun appDateInputToApi(value: String): String? =
    value.toDatePartsOrNull()?.let { parts ->
        "${parts.year}-${parts.month.toString().padStart(2, '0')}-${parts.day.toString().padStart(2, '0')}"
    }

fun appDateInputComparable(value: String): Int? =
    value.toDatePartsOrNull()?.let { it.year * 10_000 + it.month * 100 + it.day }

private fun isAppDateBefore(value: String, minimumDate: String): Boolean =
    appDateInputComparable(value)?.let { valueComparable ->
        appDateInputComparable(minimumDate)?.let { minimumComparable -> valueComparable < minimumComparable }
    } == true

private fun String.toDatePartsOrNull(): AppDateParts? {
    val trimmed = trim().replace(',', ' ')
    if (trimmed.isBlank()) return null

    val iso = Regex("""^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$""").matchEntire(trimmed)
    val numeric = Regex("""^(\d{1,2})[-/.\s]+(\d{1,2})[-/.\s]+(\d{4})$""").matchEntire(trimmed)
    val compactNumeric = Regex("""^(\d{2})(\d{2})(\d{4})$""").matchEntire(trimmed)
    val named = Regex("""^(\d{1,2})[-/.\s]*([A-Za-z]+)[-/.\s]*(\d{4})$""").matchEntire(trimmed)

    val (day, month, year) = when {
        iso != null -> Triple(iso.groupValues[3].toIntOrNull(), iso.groupValues[2].toIntOrNull(), iso.groupValues[1].toIntOrNull())
        numeric != null -> Triple(numeric.groupValues[1].toIntOrNull(), numeric.groupValues[2].toIntOrNull(), numeric.groupValues[3].toIntOrNull())
        compactNumeric != null -> Triple(compactNumeric.groupValues[1].toIntOrNull(), compactNumeric.groupValues[2].toIntOrNull(), compactNumeric.groupValues[3].toIntOrNull())
        named != null -> Triple(
            named.groupValues[1].toIntOrNull(),
            monthNumber(named.groupValues[2]),
            named.groupValues[3].toIntOrNull()
        )
        else -> return null
    }
    if (day == null || month == null || year == null || year !in 1..9999) return null
    if (month !in 1..12) return null
    val maxDay = daysInMonth(year, month)
    if (day !in 1..maxDay) return null
    return AppDateParts(year = year, month = month, day = day)
}

private fun monthNumber(value: String): Int? = when (value.lowercase().take(3)) {
    "jan" -> 1
    "feb" -> 2
    "mar" -> 3
    "apr" -> 4
    "may" -> 5
    "jun" -> 6
    "jul" -> 7
    "aug" -> 8
    "sep" -> 9
    "oct" -> 10
    "nov" -> 11
    "dec" -> 12
    else -> null
}

private fun Long.toDatePartsUtcOrNull(): AppDateParts? {
    val days = floorDiv(this, MILLIS_PER_DAY)
    return daysToDateParts(days)
}

private fun currentUtcDateParts(): AppDateParts =
    Clock.System.now().toEpochMilliseconds().toDatePartsUtcOrNull() ?: AppDateParts(2026, 1, 1)

private data class DayCell(
    val year: Int,
    val month: Int,
    val day: Int,
    val inCurrentMonth: Boolean,
    val isVisible: Boolean = true
)

private const val MILLIS_PER_DAY = 86_400_000L

private fun daysInMonth(year: Int, month: Int): Int = when (month) {
    1, 3, 5, 7, 8, 10, 12 -> 31
    4, 6, 9, 11 -> 30
    2 -> if (isLeapYear(year)) 29 else 28
    else -> 30
}

private fun isLeapYear(year: Int): Boolean =
    (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)

private fun monthGrid(year: Int, month: Int, showAdjacentMonthDays: Boolean = true): List<DayCell> {
    // The picker follows the product convention and the reference design: Monday first.
    val firstDow = (dayOfWeek(year, month, 1) + 6) % 7
    val daysCurrent = daysInMonth(year, month)
    val prev = previousMonth(year, month)
    val daysPrev = daysInMonth(prev.first, prev.second)

    val cells = mutableListOf<DayCell>()
    for (i in 0 until firstDow) {
        val day = daysPrev - firstDow + i + 1
        cells += if (showAdjacentMonthDays) DayCell(prev.first, prev.second, day, false)
        else DayCell(year, month, 1, false, isVisible = false)
    }
    for (day in 1..daysCurrent) {
        cells += DayCell(year, month, day, true, isVisible = true)
    }
    val total = if (cells.size <= 35) 35 else 42
    val next = nextMonth(year, month)
    var nextDay = 1
    while (cells.size < total) {
        cells += if (showAdjacentMonthDays) DayCell(next.first, next.second, nextDay, false)
        else DayCell(year, month, 1, false, isVisible = false)
        nextDay += 1
    }
    return cells
}

private fun previousMonth(year: Int, month: Int): Pair<Int, Int> =
    if (month == 1) year - 1 to 12 else year to month - 1

private fun nextMonth(year: Int, month: Int): Pair<Int, Int> =
    if (month == 12) year + 1 to 1 else year to month + 1

private fun dayOfWeek(year: Int, month: Int, day: Int): Int {
    val table = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
    var adjustedYear = year
    if (month < 3) adjustedYear -= 1
    val dow = (adjustedYear + adjustedYear / 4 - adjustedYear / 100 + adjustedYear / 400 + table[month - 1] + day) % 7
    return if (dow < 0) dow + 7 else dow
}

private fun datePartsToDays(year: Int, month: Int, day: Int): Long {
    var y = year
    val m = month
    val d = day
    y -= if (m <= 2) 1 else 0
    val era = floorDiv(y.toLong(), 400)
    val yoe = y - (era * 400).toInt()
    val monthIndex = m + if (m > 2) -3 else 9
    val doy = ((153 * monthIndex) + 2) / 5 + d - 1
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    return era * 146097 + doe.toLong() - 719468L
}

private fun daysToDateParts(days: Long): AppDateParts {
    val z = days + 719468L
    val era = floorDiv(z, 146097L)
    val doe = (z - era * 146097L).toInt()
    val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
    var year = yoe + (era * 400L).toInt()
    val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
    val mp = (5 * doy + 2) / 153
    val day = doy - (153 * mp + 2) / 5 + 1
    val month = mp + if (mp < 10) 3 else -9
    year += if (month <= 2) 1 else 0
    return AppDateParts(year = year, month = month, day = day)
}

private fun floorDiv(value: Long, divisor: Long): Long {
    var result = value / divisor
    val remainder = value % divisor
    if ((remainder != 0L) && ((value xor divisor) < 0L)) {
        result -= 1L
    }
    return result
}

private fun monthName(month: Int): String = when (month) {
    1 -> "Jan"
    2 -> "Feb"
    3 -> "Mar"
    4 -> "Apr"
    5 -> "May"
    6 -> "Jun"
    7 -> "Jul"
    8 -> "Aug"
    9 -> "Sep"
    10 -> "Oct"
    11 -> "Nov"
    12 -> "Dec"
    else -> "Month"
}

private fun weekLabels(): List<String> = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")

@Composable
private fun <T> CalendarPickerDropdownButton(
    text: String,
    expanded: Boolean,
    colors: AppDialogColors,
    options: List<T>,
    selectedLabel: String,
    width: Dp,
    gridColumns: Int = 1,
    popupWidthOverride: Dp? = null,
    onToggle: () -> Unit,
    onSelected: (T) -> Unit,
    onDismiss: () -> Unit,
    optionLabel: (T) -> String = { it.toString() }
) {
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val anchorWidth = with(LocalDensity.current) { anchorSize.width.toDp() }
    val selectedIndex = remember(selectedLabel, options) {
        options.indexOfFirst { optionLabel(it) == selectedLabel }.coerceAtLeast(0)
    }
    val listState = rememberLazyListState(
        initialFirstVisibleItemIndex = if (selectedIndex <= 0) 0 else (selectedIndex - 3).coerceAtLeast(0)
    )

    Box(
        modifier = Modifier.onGloballyPositioned { anchorSize = it.size }
    ) {
        Surface(
            modifier = Modifier.width(width).clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onToggle
            ),
            shape = RoundedCornerShape(12.dp),
            color = if (expanded) colors.iconTile else colors.surface,
            border = BorderStroke(1.dp, if (expanded) colors.primary else colors.border)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 10.dp, vertical = 8.dp),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = text,
                    color = colors.navy,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold
                )
                Icon(
                    painter = painterResource(Res.drawable.chevron_down),
                    contentDescription = null,
                    tint = if (expanded) colors.primary else colors.muted,
                    modifier = Modifier
                        .size(14.dp)
                        .rotate(if (expanded) 180f else 0f)
                )
            }
        }

        if (expanded) {
            Popup(
                popupPositionProvider = appDialogAnchoredPopupPositionProvider(verticalGap = 8),
                properties = PopupProperties(
                    focusable = true,
                    dismissOnBackPress = true,
                    dismissOnClickOutside = true
                ),
                onDismissRequest = onDismiss
            ) {
                Surface(
                    modifier = Modifier.width(popupWidthOverride ?: maxOf(width, anchorWidth)),
                    shape = AppDialogDropdownShape,
                    color = colors.surface,
                    shadowElevation = 12.dp,
                    border = BorderStroke(1.dp, colors.border)
                ) {
                    if (gridColumns > 1) {
                        LazyVerticalGrid(
                            columns = GridCells.Fixed(gridColumns),
                            state = rememberLazyGridState(
                                initialFirstVisibleItemIndex = (selectedIndex - gridColumns * 2).coerceAtLeast(0)
                            ),
                            modifier = Modifier.heightIn(max = 260.dp),
                            userScrollEnabled = true
                        ) {
                            items(options.size) { index ->
                                val option = options[index]
                                val label = optionLabel(option)
                                CalendarDropdownOption(
                                    label = label,
                                    selected = label == selectedLabel,
                                    colors = colors,
                                    onClick = {
                                        onSelected(option)
                                        onDismiss()
                                    }
                                )
                            }
                        }
                    } else {
                        LazyColumn(
                            state = listState,
                            modifier = Modifier.heightIn(max = 260.dp)
                        ) {
                            items(options.size) { index ->
                                val option = options[index]
                                val label = optionLabel(option)
                                CalendarDropdownOption(
                                    label = label,
                                    selected = label == selectedLabel,
                                    colors = colors,
                                    onClick = {
                                        onSelected(option)
                                        onDismiss()
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CalendarDropdownOption(
    label: String,
    selected: Boolean,
    colors: AppDialogColors,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(40.dp)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick
            ),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = label,
            color = colors.navy,
            fontSize = 14.sp,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium
        )
    }
}

@Composable
private fun CalendarNavButton(
    label: String,
    enabled: Boolean,
    colors: AppDialogColors,
    onClick: () -> Unit
) {
    Surface(
        modifier = Modifier.size(40.dp),
        shape = RoundedCornerShape(10.dp),
        color = colors.surface,
        border = BorderStroke(1.dp, if (enabled) colors.border else Color.Transparent)
    ) {
        Box(
            modifier = Modifier.clickable(
                enabled = enabled,
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick
            ),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(if (label == "‹") Res.drawable.chevron_left else Res.drawable.chevron_right),
                contentDescription = if (label == "‹") "Previous month" else "Next month",
                tint = if (enabled) colors.navy else colors.muted.copy(alpha = 0.45f),
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

@Composable
internal fun <T> AppDialogSearchSelectField(
    value: String,
    placeholder: String,
    query: String,
    expanded: Boolean,
    options: List<T>,
    optionLabel: (T) -> String,
    onQueryChange: (String) -> Unit,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelected: (T) -> Unit,
    modifier: Modifier = Modifier,
    colors: AppDialogColors = AppDialogDefaultColors,
    error: String = "",
    searchPlaceholder: String = "Search...",
    emptyLabel: String = "No options found"
) {
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val popupWidth = with(LocalDensity.current) { anchorSize.width.toDp() }
    val borderColor = when {
        error.isNotBlank() -> colors.danger
        expanded -> colors.primary
        else -> colors.inputBorder
    }

    Box(
        modifier = modifier
            .fillMaxWidth()
            .onGloballyPositioned { anchorSize = it.size }
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .height(AppDialogFieldHeight)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggleExpanded
                ),
            shape = AppDialogFieldShape,
            color = colors.surface,
            border = BorderStroke(1.dp, borderColor)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 18.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = value.ifBlank { placeholder },
                    color = if (value.isBlank()) colors.muted else colors.navy,
                    fontSize = AppDialogInputSize,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f)
                )
                Icon(
                    painter = painterResource(Res.drawable.chevron_down),
                    contentDescription = null,
                    tint = if (expanded) colors.primary else colors.navy,
                    modifier = Modifier.size(20.dp).rotate(if (expanded) 180f else 0f)
                )
            }
        }

        if (expanded) {
            Popup(
                popupPositionProvider = appDialogAnchoredPopupPositionProvider(),
                properties = PopupProperties(
                    focusable = true,
                    dismissOnBackPress = true,
                    dismissOnClickOutside = true
                ),
                onDismissRequest = onDismissExpanded
            ) {
                Surface(
                    modifier = Modifier.width(popupWidth),
                    shape = AppDialogDropdownShape,
                    color = colors.surface,
                    shadowElevation = 16.dp,
                    border = BorderStroke(1.dp, colors.border)
                ) {
                    Column {
                        AppDialogTextField(
                            value = query,
                            onValueChange = onQueryChange,
                            placeholder = searchPlaceholder,
                            colors = colors,
                            fieldHeight = 48.dp,
                            fontSize = 14.sp,
                            leadingIcon = null
                        )
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(max = 240.dp)
                                .verticalScroll(rememberScrollState())
                                .padding(vertical = 8.dp)
                        ) {
                            if (options.isEmpty()) {
                                Text(
                                    text = emptyLabel,
                                    color = colors.muted,
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.Medium,
                                    modifier = Modifier.padding(horizontal = 18.dp, vertical = 10.dp)
                                )
                            } else {
                                options.forEach { option ->
                                    val label = optionLabel(option)
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(40.dp)
                                            .background(if (label == value) colors.selectedBackground else Color.Transparent)
                                            .clickable {
                                                onSelected(option)
                                                onDismissExpanded()
                                            }
                                            .padding(horizontal = 18.dp),
                                        contentAlignment = Alignment.CenterStart
                                    ) {
                                        Text(
                                            text = label,
                                            color = colors.navy,
                                            fontSize = 14.sp,
                                            fontWeight = if (label == value) FontWeight.SemiBold else FontWeight.Medium,
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

internal fun appDialogAnchoredPopupPositionProvider(verticalGap: Int = 6): PopupPositionProvider {
    return object : PopupPositionProvider {
        override fun calculatePosition(
            anchorBounds: IntRect,
            windowSize: IntSize,
            layoutDirection: LayoutDirection,
            popupContentSize: IntSize
        ): IntOffset {
            val x = anchorBounds.left.coerceIn(0, (windowSize.width - popupContentSize.width).coerceAtLeast(0))
            val belowY = anchorBounds.bottom + verticalGap
            val aboveY = anchorBounds.top - popupContentSize.height - verticalGap
            val y = if (belowY + popupContentSize.height <= windowSize.height) belowY else aboveY.coerceAtLeast(0)
            return IntOffset(x, y)
        }
    }
}
